
import { useAuth } from '../contexts/AuthContext';

const API_URL = import.meta.env.VITE_API_URL;

export class ApiError extends Error {
    status: number;
    body: any;
    constructor(message: string, status: number, body?: any) {
        super(message);
        this.name = 'ApiError';
        this.status = status;
        this.body = body;
    }
}

async function buildApiError(response: Response, fallbackMessage: string): Promise<ApiError> {
    let body: any = undefined;
    try {
        body = await response.json();
    } catch {
        // response body wasn't JSON (or was empty) — ignore, fall back to status text
    }
    const message = body?.message || fallbackMessage;
    return new ApiError(message, response.status, body);
}

async function getAuthHeaders(firebaseUser: any) {
    if (!firebaseUser) {
        throw new Error('Not authenticated');
    }
    const token = await firebaseUser.getIdToken();
    return {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
    };
}

export const api = {
    // Departments
    async getDepartments() {
        const { firebaseUser } = useAuth();
        const headers = await getAuthHeaders(firebaseUser);
        const response = await fetch(`${API_URL}/departments`, { headers });
        if (!response.ok) throw new Error('Failed to fetch departments');
        return response.json();
    },

    // Games
    async getGames(departmentId?: string) {
        const { firebaseUser } = useAuth();
        const headers = await getAuthHeaders(firebaseUser);
        const url = departmentId
            ? `${API_URL}/games?departmentId=${departmentId}`
            : `${API_URL}/games`;
        const response = await fetch(url, { headers });
        if (!response.ok) throw new Error('Failed to fetch games');
        return response.json();
    },

    async getGame(gameId: string) {
        const { firebaseUser } = useAuth();
        const headers = await getAuthHeaders(firebaseUser);
        const response = await fetch(`${API_URL}/games/${gameId}`, { headers });
        if (!response.ok) throw new Error('Failed to fetch game');
        return response.json();
    },

    async createGame(data: any) {
        const { firebaseUser } = useAuth();
        const headers = await getAuthHeaders(firebaseUser);
        const response = await fetch(`${API_URL}/games`, {
            method: 'POST',
            headers,
            body: JSON.stringify(data),
        });
        if (!response.ok) throw new Error('Failed to create game');
        return response.json();
    },

    // Items
    async getItems() {
        const { firebaseUser } = useAuth();
        const headers = await getAuthHeaders(firebaseUser);
        const response = await fetch(`${API_URL}/items`, { headers });
        if (!response.ok) throw new Error('Failed to fetch items');
        return response.json();
    },

    async createItem(data: any) {
        const { firebaseUser } = useAuth();
        const headers = await getAuthHeaders(firebaseUser);
        const response = await fetch(`${API_URL}/items`, {
            method: 'POST',
            headers,
            body: JSON.stringify(data),
        });
        if (!response.ok) throw new Error('Failed to create item');
        return response.json();
    },

    async updateItem(id: string, data: any) {
        const { firebaseUser } = useAuth();
        const headers = await getAuthHeaders(firebaseUser);
        const response = await fetch(`${API_URL}/items/${id}`, {
            method: 'PUT',
            headers,
            body: JSON.stringify(data),
        });
        if (!response.ok) throw new Error('Failed to update item');
        return response.json();
    },

    async deleteItem(id: string) {
        const { firebaseUser } = useAuth();
        const headers = await getAuthHeaders(firebaseUser);
        const response = await fetch(`${API_URL}/items/${id}`, {
            method: 'DELETE',
            headers,
        });
        if (!response.ok) throw new Error('Failed to delete item');
        return response.json();
    },

    // Claims
    async createClaim(data: any) {
        const { firebaseUser } = useAuth();
        const headers = await getAuthHeaders(firebaseUser);
        const response = await fetch(`${API_URL}/claims`, {
            method: 'POST',
            headers,
            body: JSON.stringify(data),
        });
        if (!response.ok) throw new Error('Failed to create claim');
        return response.json();
    },

    async getClaims(status?: string) {
        const { firebaseUser } = useAuth();
        const headers = await getAuthHeaders(firebaseUser);
        const url = status
            ? `${API_URL}/claims?status=${status}`
            : `${API_URL}/claims`;
        const response = await fetch(url, { headers });
        if (!response.ok) throw new Error('Failed to fetch claims');
        return response.json();
    },

    async approveClaim(claimId: string) {
        const { firebaseUser } = useAuth();
        const headers = await getAuthHeaders(firebaseUser);
        const response = await fetch(`${API_URL}/claims/${claimId}/approve`, {
            method: 'PUT',
            headers,
        });
        if (!response.ok) throw new Error('Failed to approve claim');
        return response.json();
    },

    // Activity
    async getActivity(filters?: {
        departmentId?: string;
        gameId?: string;
        eventType?: string;
        page?: number;
        pageSize?: number;
    }) {
        const { firebaseUser } = useAuth();
        const headers = await getAuthHeaders(firebaseUser);
        const params = new URLSearchParams();
        if (filters?.departmentId) params.append('departmentId', filters.departmentId);
        if (filters?.gameId) params.append('gameId', filters.gameId);
        if (filters?.eventType) params.append('eventType', filters.eventType);
        if (filters?.page !== undefined) params.append('page', filters.page.toString());
        if (filters?.pageSize) params.append('pageSize', filters.pageSize.toString());

        const response = await fetch(`${API_URL}/activity?${params}`, { headers });
        if (!response.ok) throw new Error('Failed to fetch activity');
        return response.json();
    },
};

// Hook-based API client
export function useApi() {
    const { firebaseUser } = useAuth();

    async function getHeaders() {
        if (!firebaseUser) {
            throw new Error('Not authenticated');
        }
        const token = await firebaseUser.getIdToken();
        return {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
        };
    }

    function buildFilterParams(filters?: { gameId?: string; startDate?: string; endDate?: string; status?: string }): string {
        if (!filters) return '';
        const params = new URLSearchParams();
        if (filters.gameId) params.append('gameId', filters.gameId);
        if (filters.startDate) params.append('startDate', filters.startDate);
        if (filters.endDate) params.append('endDate', filters.endDate);
        if (filters.status) params.append('status', filters.status);
        const str = params.toString();
        return str ? '?' + str : '';
    }

    return {
        async getDepartments() {
            const headers = await getHeaders();
            const response = await fetch(`${API_URL}/departments`, { headers });
            if (!response.ok) throw new Error('Failed to fetch departments');
            return response.json();
        },

        async createDepartment(data: any) {
            const headers = await getHeaders();
            const response = await fetch(`${API_URL}/departments`, {
                method: 'POST',
                headers,
                body: JSON.stringify(data),
            });
            if (!response.ok) throw new Error('Failed to create department');
            return response.json();
        },

        async getGames(departmentId?: string) {
            const headers = await getHeaders();
            const url = departmentId
                ? `${API_URL}/games?departmentId=${departmentId}`
                : `${API_URL}/games`;
            const response = await fetch(url, { headers });
            if (!response.ok) throw new Error('Failed to fetch games');
            return response.json();
        },

        async getGame(gameId: string) {
            const headers = await getHeaders();
            const response = await fetch(`${API_URL}/games/${gameId}`, { headers });
            if (!response.ok) throw new Error('Failed to fetch game');
            return response.json();
        },

        async createGame(data: any) {
            const headers = await getHeaders();
            const response = await fetch(`${API_URL}/games`, {
                method: 'POST',
                headers,
                body: JSON.stringify(data),
            });
            if (!response.ok) throw new Error('Failed to create game');
            return response.json();
        },

        async updateGame(id: string, data: any) {
            const headers = await getHeaders();
            const response = await fetch(`${API_URL}/games/${id}`, {
                method: 'PUT',
                headers,
                body: JSON.stringify(data),
            });
            if (!response.ok) throw new Error('Failed to update game');
            return response.json();
        },

        async startGame(gameId: string) {
            const headers = await getHeaders();
            console.log('Starting game request:', gameId);
            const response = await fetch(`${API_URL}/games/${gameId}/start`, {
                method: 'POST',
                headers,
            });
            console.log('Start game response status:', response.status);

            if (!response.ok) {
                const text = await response.text();
                console.log('Start game error body:', text);
                try {
                    const errorData = JSON.parse(text);
                    throw new Error(errorData.message || errorData.error || 'Failed to start game');
                } catch (e) {
                    // If parsing fails, use the raw text if it's short, otherwise generic error
                    const msg = text.length < 100 ? text : 'Failed to start game (invalid response)';
                    throw new Error(msg);
                }
            }
            return response.json();
        },

        async getBingoCard(gameId: string) {
            const headers = await getHeaders();
            const response = await fetch(`${API_URL}/games/${gameId}/card`, { headers });
            if (!response.ok) {
                if (response.status === 404) return { card: null };
                throw new Error('Failed to fetch bingo card');
            }
            return response.json();
        },

        async getGameLeaderboard(gameId: string, ownOnly: boolean = false) {
            const headers = await getHeaders();
            const url = ownOnly
                ? `${API_URL}/games/${gameId}/leaderboard?own=true`
                : `${API_URL}/games/${gameId}/leaderboard`;
            const response = await fetch(url, { headers });
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || 'Failed to fetch leaderboard');
            }
            return response.json();
        },

        async getMyGameStats(gameId: string) {
            const headers = await getHeaders();
            const response = await fetch(`${API_URL}/games/${gameId}/my-stats`, { headers });
            if (!response.ok) throw new Error('Failed to fetch stats');
            return response.json();
        },

        async getGameParticipants(gameId: string) {
            const headers = await getHeaders();
            const response = await fetch(`${API_URL}/games/${gameId}/participants`, { headers });
            if (!response.ok) throw new Error('Failed to fetch participants');
            return response.json();
        },

        async getItems() {
            const headers = await getHeaders();
            const response = await fetch(`${API_URL}/items`, { headers });
            if (!response.ok) throw new Error('Failed to fetch items');
            return response.json();
        },

        async createItem(data: any) {
            const headers = await getHeaders();
            const response = await fetch(`${API_URL}/items`, {
                method: 'POST',
                headers,
                body: JSON.stringify(data),
            });
            if (!response.ok) throw new Error('Failed to create item');
            return response.json();
        },

        async updateItem(id: string, data: any) {
            const headers = await getHeaders();
            const response = await fetch(`${API_URL}/items/${id}`, {
                method: 'PUT',
                headers,
                body: JSON.stringify(data),
            });
            if (!response.ok) throw new Error('Failed to update item');
            return response.json();
        },

        async deleteItem(id: string) {
            const headers = await getHeaders();
            const response = await fetch(`${API_URL}/items/${id}`, {
                method: 'DELETE',
                headers,
            });
            if (!response.ok) throw new Error('Failed to delete item');
            return response.json();
        },

        async createClaim(data: any) {
            const headers = await getHeaders();
            const response = await fetch(`${API_URL}/claims`, {
                method: 'POST',
                headers,
                body: JSON.stringify(data),
            });
            if (!response.ok) throw new Error('Failed to create claim');
            return response.json();
        },

        async getClaims(status?: string) {
            const headers = await getHeaders();
            const url = status
                ? `${API_URL}/claims?status=${status}`
                : `${API_URL}/claims`;
            const response = await fetch(url, { headers });
            if (!response.ok) throw new Error('Failed to fetch claims');
            return response.json();
        },

        async getMyClaims(filters?: { gameId?: string; startDate?: string; endDate?: string; status?: string }) {
            const headers = await getHeaders();
            const response = await fetch(`${API_URL}/claims/mine${buildFilterParams(filters)}`, { headers });
            if (!response.ok) throw await buildApiError(response, 'Failed to fetch claims');
            return response.json();
        },

        async getEmployeeClaims(employeeId: string, filters?: { gameId?: string; startDate?: string; endDate?: string; status?: string }) {
            const headers = await getHeaders();
            const response = await fetch(`${API_URL}/employees/${employeeId}/claims${buildFilterParams(filters)}`, { headers });
            if (!response.ok) throw await buildApiError(response, 'Failed to fetch employee claims');
            return response.json();
        },

        async approveClaim(claimId: string) {
            const headers = await getHeaders();
            const response = await fetch(`${API_URL}/claims/${claimId}/approve`, {
                method: 'PUT',
                headers,
            });
            if (!response.ok) throw new Error('Failed to approve claim');
            return response.json();
        },

        async rejectClaim(claimId: string, reason?: string) {
            const headers = await getHeaders();
            const response = await fetch(`${API_URL}/claims/${claimId}/reject`, {
                method: 'PUT',
                headers,
                body: JSON.stringify({ reason }),
            });
            if (!response.ok) throw new Error('Failed to reject claim');
            return response.json();
        },

        async deleteClaim(claimId: string) {
            const headers = await getHeaders();
            const response = await fetch(`${API_URL}/claims/${claimId}`, {
                method: 'DELETE',
                headers,
            });
            if (!response.ok) throw new Error('Failed to delete claim');
            return response.json();
        },

        async updateClaim(claimId: string, data: { description?: string; evidenceUrls?: string[]; imageUrls?: string[] }) {
            const headers = await getHeaders();
            const response = await fetch(`${API_URL}/claims/${claimId}`, {
                method: 'PUT',
                headers,
                body: JSON.stringify(data),
            });
            if (!response.ok) throw new Error('Failed to update claim');
            return response.json();
        },

        async getEmployees() {
            const headers = await getHeaders();
            const response = await fetch(`${API_URL}/employees`, { headers });
            if (!response.ok) throw new Error('Failed to fetch employees');
            return response.json();
        },

        async createEmployee(data: any) {
            const headers = await getHeaders();
            const response = await fetch(`${API_URL}/employees`, {
                method: 'POST',
                headers,
                body: JSON.stringify(data),
            });
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || 'Failed to create employee');
            }
            return response.json();
        },

        async updateEmployee(id: string, data: any) {
            const headers = await getHeaders();
            const response = await fetch(`${API_URL}/employees/${id}`, {
                method: 'PUT',
                headers,
                body: JSON.stringify(data),
            });
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || 'Failed to update employee');
            }
            return response.json();
        },

        async updateDepartment(id: string, data: { name?: string; description?: string }) {
            const headers = await getHeaders();
            const response = await fetch(`${API_URL}/departments/${id}`, {
                method: 'PUT',
                headers,
                body: JSON.stringify(data),
            });
            if (!response.ok) throw new Error('Failed to update department');
            return response.json();
        },

        async getDepartmentMembers(departmentId: string) {
            const headers = await getHeaders();
            const response = await fetch(`${API_URL}/departments/${departmentId}/members`, { headers });
            if (!response.ok) throw new Error('Failed to fetch department members');
            return response.json();
        },

        async assignDepartments(employeeId: string, departmentIds: string[]) {
            const headers = await getHeaders();
            const response = await fetch(`${API_URL}/employees/${employeeId}/departments`, {
                method: 'POST',
                headers,
                body: JSON.stringify({ departmentIds }),
            });
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || 'Failed to assign departments');
            }
            return response.json();
        },

        async getActivity(filters?: any) {
            const headers = await getHeaders();
            const params = new URLSearchParams();
            if (filters?.departmentId) params.append('departmentId', filters.departmentId);
            if (filters?.gameId) params.append('gameId', filters.gameId);
            if (filters?.page !== undefined) params.append('page', filters.page.toString());

            const response = await fetch(`${API_URL}/activity?${params}`, { headers });
            if (!response.ok) throw new Error('Failed to fetch activity');
            return response.json();
        },

        async updateProfile(data: { name: string; position?: string }) {
            const headers = await getHeaders();
            const response = await fetch(`${API_URL}/users/profile`, {
                method: 'PUT',
                headers,
                body: JSON.stringify(data),
            });
            if (!response.ok) throw new Error('Failed to update profile');
            return response.json();
        },

        async getStats() {
            const headers = await getHeaders();
            const response = await fetch(`${API_URL}/admin/stats`, { headers });
            if (!response.ok) throw new Error('Failed to fetch stats');
            return response.json();
        },

        async getRandomTip() {
            const headers = await getHeaders();
            const response = await fetch(`${API_URL}/tips/random`, { headers });
            if (!response.ok) throw new Error('Failed to fetch tip');
            return response.json();
        },

        async getTips(filters?: { category?: string; isActive?: boolean }) {
            const headers = await getHeaders();
            const params = new URLSearchParams();
            if (filters?.category) params.append('category', filters.category);
            if (filters?.isActive !== undefined) params.append('isActive', filters.isActive.toString());

            const url = params.toString() ? `${API_URL}/tips?${params}` : `${API_URL}/tips`;
            const response = await fetch(url, { headers });
            if (!response.ok) throw new Error('Failed to fetch tips');
            return response.json();
        },

        async createTip(data: { title: string; content: string; category: string; isActive?: boolean }) {
            const headers = await getHeaders();
            const response = await fetch(`${API_URL}/tips`, {
                method: 'POST',
                headers,
                body: JSON.stringify(data),
            });
            if (!response.ok) throw new Error('Failed to create tip');
            return response.json();
        },

        async updateTip(id: string, data: { title?: string; content?: string; category?: string; isActive?: boolean }) {
            const headers = await getHeaders();
            const response = await fetch(`${API_URL}/tips/${id}`, {
                method: 'PUT',
                headers,
                body: JSON.stringify(data),
            });
            if (!response.ok) throw new Error('Failed to update tip');
            return response.json();
        },

        async deleteTip(id: string) {
            const headers = await getHeaders();
            const response = await fetch(`${API_URL}/tips/${id}`, {
                method: 'DELETE',
                headers,
            });
            if (!response.ok) throw new Error('Failed to delete tip');
            return response.json();
        },

        async updateTheme(data: {
            logoUrl?: string;
            primaryColor?: string;
            secondaryColor?: string;
            backgroundColor?: string;
            accentColor?: string;
            fontFamily?: string;
        }) {
            const headers = await getHeaders();
            const response = await fetch(`${API_URL}/tenant/theme`, {
                method: 'PUT',
                headers,
                body: JSON.stringify(data),
            });
            if (!response.ok) throw new Error('Failed to update theme');
            return response.json();
        },

        async updateSlackSettings(data: { slackWebhookUrl?: string; testConnection?: boolean }) {
            const headers = await getHeaders();
            const response = await fetch(`${API_URL}/tenant/slack`, {
                method: 'PUT',
                headers,
                body: JSON.stringify(data),
            });
            if (!response.ok) {
                const error = await response.json().catch(() => ({ error: 'Failed to update Slack settings' }));
                throw new Error(error.error || 'Failed to update Slack settings');
            }
            return response.json();
        },

        // Leave Tracker
        async getLeaveTypes() {
            const headers = await getHeaders();
            const response = await fetch(`${API_URL}/leaves/types`, { headers });
            if (!response.ok) throw new Error('Failed to fetch leave types');
            return response.json();
        },

        async createLeaveType(data: any) {
            const headers = await getHeaders();
            const response = await fetch(`${API_URL}/leaves/types`, {
                method: 'POST',
                headers,
                body: JSON.stringify(data),
            });
            if (!response.ok) throw new Error('Failed to create leave type');
            return response.json();
        },

        async updateLeaveType(id: string, data: any) {
            const headers = await getHeaders();
            const response = await fetch(`${API_URL}/leaves/types/${id}`, {
                method: 'PUT',
                headers,
                body: JSON.stringify(data),
            });
            if (!response.ok) throw new Error('Failed to update leave type');
            return response.json();
        },

        async deleteLeaveType(id: string) {
            const headers = await getHeaders();
            const response = await fetch(`${API_URL}/leaves/types?id=${id}`, {
                method: 'DELETE',
                headers,
            });
            if (!response.ok) throw new Error('Failed to delete leave type');
            return response.json();
        },

        async getLeaveBalances(userId?: string) {
            const headers = await getHeaders();
            const url = userId ? `${API_URL}/leaves/balance?userId=${userId}` : `${API_URL}/leaves/balance`;
            const response = await fetch(url, { headers });
            if (!response.ok) throw new Error('Failed to fetch leave balances');
            return response.json();
        },

        async getLeaveRequests(filters?: { userId?: string; status?: string; startDate?: Date; endDate?: Date }) {
            const headers = await getHeaders();
            const params = new URLSearchParams();
            if (filters?.userId) params.append('userId', filters.userId);
            if (filters?.status) params.append('status', filters.status);
            if (filters?.startDate) params.append('start', filters.startDate.toISOString());
            if (filters?.endDate) params.append('end', filters.endDate.toISOString());

            const response = await fetch(`${API_URL}/leaves/requests?${params}`, { headers });
            if (!response.ok) {
                console.error('getLeaveRequests failed:', response.status, response.statusText);
                const text = await response.text();
                console.error('Response body:', text);
                throw new Error(`Failed to fetch leave requests: ${response.status} ${response.statusText}`);
            }
            return response.json();
        },

        async createLeaveRequest(data: any) {
            const headers = await getHeaders();
            const response = await fetch(`${API_URL}/leaves/requests`, {
                method: 'POST',
                headers,
                body: JSON.stringify(data),
            });
            if (!response.ok) throw new Error('Failed to create leave request');
            return response.json();
        },

        async updateLeaveStatus(id: string, status: string, rejectionReason?: string) {
            const headers = await getHeaders();
            const response = await fetch(`${API_URL}/leaves/requests/${id}/approve`, {
                method: 'PUT',
                headers,
                body: JSON.stringify({ status, rejectionReason }),
            });
            if (!response.ok) throw new Error('Failed to update leave status');
            return response.json();
        },

        async getHolidays(start?: Date, end?: Date) {
            const headers = await getHeaders();
            const params = new URLSearchParams();
            if (start) params.append('start', start.toISOString());
            if (end) params.append('end', end.toISOString());

            const response = await fetch(`${API_URL}/leaves/holidays?${params}`, { headers });
            if (!response.ok) throw new Error('Failed to fetch holidays');
            return response.json();
        },

        async createHoliday(data: any) {
            const headers = await getHeaders();
            const response = await fetch(`${API_URL}/leaves/holidays`, {
                method: 'POST',
                headers,
                body: JSON.stringify(data),
            });
            if (!response.ok) throw new Error('Failed to create holiday');
            return response.json();
        },

        async updateHoliday(id: string, data: any) {
            const headers = await getHeaders();
            const response = await fetch(`${API_URL}/leaves/holidays/${id}`, {
                method: 'PUT',
                headers,
                body: JSON.stringify(data),
            });
            if (!response.ok) throw new Error('Failed to update holiday');
            return response.json();
        },

        async deleteHoliday(id: string) {
            const headers = await getHeaders();
            const response = await fetch(`${API_URL}/leaves/holidays?id=${id}`, {
                method: 'DELETE',
                headers,
            });
            if (!response.ok) throw new Error('Failed to delete holiday');
            return response.json();
        },

        async getLeaveCalendar(start?: Date, end?: Date) {
            const headers = await getHeaders();
            const params = new URLSearchParams();
            if (start) params.append('start', start.toISOString());
            if (end) params.append('end', end.toISOString());

            const response = await fetch(`${API_URL}/leaves/calendar?${params}`, { headers });
            if (!response.ok) throw new Error('Failed to fetch calendar');
            return response.json();
        },

        // Invitations
        async getInvitations() {
            const headers = await getHeaders();
            const response = await fetch(`${API_URL}/invitations`, { headers });
            if (!response.ok) throw new Error('Failed to fetch invitations');
            return response.json();
        },

        async createInvitation(email: string) {
            const headers = await getHeaders();
            const response = await fetch(`${API_URL}/invitations`, {
                method: 'POST',
                headers,
                body: JSON.stringify({ email }),
            });
            if (!response.ok) throw new Error('Failed to create invitation');
            return response.json();
        },

        async deleteInvitation(id: string) {
            const headers = await getHeaders();
            const response = await fetch(`${API_URL}/invitations/${id}`, {
                method: 'DELETE',
                headers,
            });
            if (!response.ok) throw new Error('Failed to delete invitation');
            return response.json();
        },

        async sendInvitationReminder(id: string) {
            const headers = await getHeaders();
            const response = await fetch(`${API_URL}/invitations/${id}/remind`, {
                method: 'POST',
                headers,
            });
            if (!response.ok) throw new Error('Failed to send reminder');
            return response.json();
        },

        // Employee Approval
        async approveEmployee(id: string) {
            const headers = await getHeaders();
            const response = await fetch(`${API_URL}/employees/${id}/approve`, {
                method: 'PUT',
                headers,
            });
            if (!response.ok) throw new Error('Failed to approve employee');
            return response.json();
        },

        // Domain settings
        async updateDomainSettings(data: { allowedDomains: string[]; requireApproval: boolean }) {
            const headers = await getHeaders();
            const response = await fetch(`${API_URL}/tenant/domains`, {
                method: 'PUT',
                headers,
                body: JSON.stringify(data),
            });
            if (!response.ok) throw new Error('Failed to update domain settings');
            return response.json();
        },

        async uploadFile(file: File): Promise<{ url: string; key: string }> {
            const headers = await getHeaders();

            // Read file as base64
            const buffer = await file.arrayBuffer();
            const base64 = btoa(
                new Uint8Array(buffer).reduce((data, byte) => data + String.fromCharCode(byte), '')
            );

            const response = await fetch(`${API_URL}/upload`, {
                method: 'POST',
                headers,
                body: JSON.stringify({
                    fileName: file.name,
                    contentType: file.type,
                    data: base64,
                }),
            });

            if (!response.ok) {
                const error = await response.json().catch(() => ({ message: 'Upload failed' }));
                throw new Error(error.message || 'Failed to upload file');
            }
            return response.json();
        },
    };
}

// ============================================
// Super Admin API
// ============================================

async function getSuperAdminHeaders() {
    // Get Firebase auth from any available app (super-admin or default)
    const { getAuth } = await import('firebase/auth');
    const { getApps } = await import('firebase/app');

    const apps = getApps();
    if (apps.length === 0) throw new Error('Firebase not initialized');

    // Use the first available app (could be 'super-admin' or default)
    const auth = getAuth(apps[0]);
    const user = auth.currentUser;
    if (!user) throw new Error('Not authenticated');
    const idToken = await user.getIdToken();
    return {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`,
    };
}

export const superAdminApi = {
    // Tenants
    async listTenants() {
        const headers = await getSuperAdminHeaders();
        const response = await fetch(`${API_URL}/super-admin/tenants`, { headers });
        if (!response.ok) throw new Error('Failed to fetch tenants');
        return response.json();
    },

    async getTenant(id: string) {
        const headers = await getSuperAdminHeaders();
        const response = await fetch(`${API_URL}/super-admin/tenants/${id}`, { headers });
        if (!response.ok) throw new Error('Failed to fetch tenant');
        return response.json();
    },

    async updateTenant(id: string, data: any) {
        const headers = await getSuperAdminHeaders();
        const response = await fetch(`${API_URL}/super-admin/tenants/${id}`, {
            method: 'PUT',
            headers,
            body: JSON.stringify(data),
        });
        if (!response.ok) throw new Error('Failed to update tenant');
        return response.json();
    },

    // Plans
    async listPlans(region?: string) {
        const headers = await getSuperAdminHeaders();
        const url = region
            ? `${API_URL}/super-admin/plans?region=${region}`
            : `${API_URL}/super-admin/plans`;
        const response = await fetch(url, { headers });
        if (!response.ok) throw new Error('Failed to fetch plans');
        return response.json();
    },

    async createPlan(data: any) {
        const headers = await getSuperAdminHeaders();
        const response = await fetch(`${API_URL}/super-admin/plans`, {
            method: 'POST',
            headers,
            body: JSON.stringify(data),
        });
        if (!response.ok) throw new Error('Failed to create plan');
        return response.json();
    },

    async updatePlan(id: string, data: any) {
        const headers = await getSuperAdminHeaders();
        const response = await fetch(`${API_URL}/super-admin/plans/${id}`, {
            method: 'PUT',
            headers,
            body: JSON.stringify(data),
        });
        if (!response.ok) throw new Error('Failed to update plan');
        return response.json();
    },

    // Modules
    async listModules() {
        const headers = await getSuperAdminHeaders();
        const response = await fetch(`${API_URL}/super-admin/modules`, { headers });
        if (!response.ok) throw new Error('Failed to fetch modules');
        return response.json();
    },

    // Audit Logs
    async listAuditLogs(options: { limit?: number; offset?: number; action?: string } = {}) {
        const headers = await getSuperAdminHeaders();
        const params = new URLSearchParams();
        if (options.limit) params.append('limit', String(options.limit));
        if (options.offset) params.append('offset', String(options.offset));
        if (options.action) params.append('action', options.action);

        const url = `${API_URL}/super-admin/audit-logs${params.toString() ? '?' + params.toString() : ''}`;
        const response = await fetch(url, { headers });
        if (!response.ok) throw new Error('Failed to fetch audit logs');
        return response.json();
    },
};
